<template>
	<Skeleton :paragraph="{ rows: 7 }" :title="false" :loading="isLoading">
		<VerticalLayout>
			<RadioGroup
				v-model:value="daysValue"
				:options="daysOptions"
				option-type="button"
				@change="onDaysChange"
			/>
			<div class="scrollable-x">
				<Table
					:columns="columns"
					:data-source="items"
					:row-key="record => record.command_name"
					:pagination="false"
					size="small"
				>
					<template #bodyCell="{ column, record }">
						<template v-if="column.dataIndex === 'last_run'">
							<DateTimeDisplay :date="record.last_run" hideUtc />
						</template>
						<template v-else-if="column.dataIndex === 'avg_duration_seconds'">
							<span
								v-if="record.avg_duration_seconds != null"
								class="clickable-cell"
								@click="onMetricClick(record, 'duration_seconds')"
							>
								{{ record.avg_duration_seconds.toFixed(2) }}
							</span>
							<span v-else>-</span>
						</template>
						<template v-else-if="column.dataIndex === 'max_duration_seconds'">
							<span
								v-if="record.max_duration_seconds != null"
								class="clickable-cell"
								@click="onMetricClick(record, 'duration_seconds')"
							>
								{{ record.max_duration_seconds.toFixed(2) }}
							</span>
							<span v-else>-</span>
						</template>
						<template v-else-if="column.dataIndex === 'avg_memory_bytes'">
							<span
								v-if="record.avg_memory_bytes != null"
								class="clickable-cell"
								@click="onMetricClick(record, 'memory_bytes')"
							>
								{{ formatBytes(record.avg_memory_bytes) }}
							</span>
							<span v-else>-</span>
						</template>
						<template v-else-if="column.dataIndex === 'max_memory_bytes'">
							<span
								v-if="record.max_memory_bytes != null"
								class="clickable-cell"
								@click="onMetricClick(record, 'memory_bytes')"
							>
								{{ formatBytes(record.max_memory_bytes) }}
							</span>
							<span v-else>-</span>
						</template>
						<template v-else-if="column.dataIndex === 'avg_cpu_seconds'">
							<span
								v-if="record.avg_cpu_seconds != null"
								class="clickable-cell"
								@click="onMetricClick(record, 'cpu_seconds')"
							>
								{{ record.avg_cpu_seconds.toFixed(2) }}
							</span>
							<span v-else>-</span>
						</template>
						<template v-else-if="column.dataIndex === 'max_cpu_seconds'">
							<span
								v-if="record.max_cpu_seconds != null"
								class="clickable-cell"
								@click="onMetricClick(record, 'cpu_seconds')"
							>
								{{ record.max_cpu_seconds.toFixed(2) }}
							</span>
							<span v-else>-</span>
						</template>
						<template v-else-if="column.dataIndex === 'avg_thread_count'">
							<span
								v-if="record.avg_thread_count != null"
								class="clickable-cell"
								@click="onMetricClick(record, 'thread_count')"
							>
								{{ record.avg_thread_count.toFixed(2) }}
							</span>
							<span v-else>-</span>
						</template>
						<template v-else-if="column.dataIndex === 'max_thread_count'">
							<span
								v-if="record.max_thread_count != null"
								class="clickable-cell"
								@click="onMetricClick(record, 'thread_count')"
							>
								{{ record.max_thread_count }}
							</span>
							<span v-else>-</span>
						</template>
						<template v-else-if="column.dataIndex === 'avg_open_files_count'">
							<span
								v-if="record.avg_open_files_count != null"
								class="clickable-cell"
								@click="onMetricClick(record, 'open_files_count')"
							>
								{{ record.avg_open_files_count.toFixed(2) }}
							</span>
							<span v-else>-</span>
						</template>
						<template v-else-if="column.dataIndex === 'max_open_files_count'">
							<span
								v-if="record.max_open_files_count != null"
								class="clickable-cell"
								@click="onMetricClick(record, 'open_files_count')"
							>
								{{ record.max_open_files_count }}
							</span>
							<span v-else>-</span>
						</template>
					</template>
				</Table>
			</div>
			<div v-if="showDetailsChart">
				<TimeSeriesChart
					:isLoading="isDetailsLoading"
					:series="detailsSeries"
					:options="detailsOptions"
					:height="400"
				/>
			</div>
		</VerticalLayout>
	</Skeleton>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { Skeleton, Table, RadioGroup } from 'ant-design-vue';
import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';
import Consts from '@/api/Consts';

import { getBaseOptions, nullSafeFormatter } from '@/utils/chartsUtils';

import DateTimeDisplay from '@/components/common/DateTimeDisplay';
import VerticalLayout from '@/components/common/VerticalLayout';
import TimeSeriesChart from '@/components/stats/TimeSeriesChart';

export default {
	components: {
		Skeleton, Table, RadioGroup, DateTimeDisplay, VerticalLayout, TimeSeriesChart,
	},
	setup() {
		const { t } = useI18n();
		const isLoading = ref(false);
		const items = ref([]);
		const columns = Consts.performanceTableColumnsComp;
		const daysValue = ref(1);
		const daysOptions = computed(() => [
			{ label: t('settings.performance.daysOptions.day1'), value: 1 },
			{ label: t('settings.performance.daysOptions.week1'), value: 7 },
		]);

		const isDetailsLoading = ref(false);
		const detailsSeries = ref([]);
		const detailsOptions = ref(null);
		const selectedMetric = ref(null);

		const showDetailsChart = computed(() => detailsSeries.value.length > 0 || isDetailsLoading.value);
		watch(daysValue, () => {
			detailsSeries.value = []
		});

		const formatBytes = bytes => {
			if (bytes == null) return '-';
			const mbytes = bytes / (1024 * 1024);
			return mbytes.toFixed(2);
		};

		const buildDetailsOptions = () => {
			const options = getBaseOptions('performanceDetails', detailsSeries.value);
			if (selectedMetric.value === 'memory_bytes') {
				options.yaxis.title.text = 'megabytes';
				options.tooltip.y.formatter = nullSafeFormatter(value => formatBytes(value) + ' MB');
			} else if (selectedMetric.value === 'duration_seconds' || selectedMetric.value === 'cpu_seconds') {
				options.yaxis.title.text = 'seconds';
				options.tooltip.y.formatter = nullSafeFormatter(value => value.toFixed(2) + ' s');
			} else {
				options.yaxis.title.text = 'count';
				options.tooltip.y.formatter = nullSafeFormatter(value => Math.round(value));
			}
			detailsOptions.value = options;
		};

		const fetchData = () => {
			isLoading.value = true;
			Api.utilsPerformanceGet(daysValue.value, response => {
				if (response.status === 'ok') {
					items.value = response.items || [];
				}
				isLoading.value = false;
			});
		};

		const onDaysChange = () => {
			fetchData();
		};

		const onMetricClick = (record, metric) => {
			isDetailsLoading.value = true;
			selectedMetric.value = metric;
			Api.utilsPerformanceDetailsGet(daysValue.value, record.command_name, metric, response => {
				if (response.status === 'ok') {
					const itemsLocal = response.items || [];
					const data = itemsLocal.map(item => [new Date(item.run_at).getTime(), item.value]);
					detailsSeries.value = [{
						name: record.command_name,
						data,
					}];
					buildDetailsOptions();
				}
				isDetailsLoading.value = false;
			});
		};

		onMounted(fetchData);

		return {
			isLoading,
			items,
			columns,
			daysValue,
			daysOptions,
			onDaysChange,
			formatBytes,
			isDetailsLoading,
			detailsSeries,
			detailsOptions,
			showDetailsChart,
			onMetricClick,
		};
	},
}
</script>

<style scoped>
.clickable-cell {
	cursor: pointer;
}
</style>
